import { useRef, useEffect, useState, useCallback } from 'react';
import useDrawStore, { TOOL_TYPES, ELEMENT_TYPES } from '@/stores/drawStore';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';

// Constants
const HANDLE_SIZE = 8;
const ROTATION_HANDLE_OFFSET = 25;
const CONNECTION_POINT_SIZE = 6;

export default function DrawCanvas({ className, onCursorMove }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [startPoint, setStartPoint] = useState(null);
  const [currentShape, setCurrentShape] = useState(null);
  const [freehandPoints, setFreehandPoints] = useState([]);
  const [loadedImages, setLoadedImages] = useState({});
  const [connectionStart, setConnectionStart] = useState(null);
  const [connectionEnd, setConnectionEnd] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  
  // Text editing state
  const [editingText, setEditingText] = useState(null); // { elementId, type: 'icon' | 'text', x, y, value }
  const textInputRef = useRef(null);
  
  const {
    elements,
    selectedIds,
    activeTool,
    activeStyles,
    zoom,
    panX,
    panY,
    showGrid,
    snapToGrid,
    gridSize,
    isDarkMode,
    collaborators,
    addElement,
    updateElement,
    deleteElements,
    setSelectedIds,
    clearSelection,
    setActiveTool,
    selectAll,
  } = useDrawStore();

  const selectedElements = elements.filter(el => selectedIds.includes(el.id));

  // Get mouse position
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - panX) / zoom,
      y: (e.clientY - rect.top - panY) / zoom,
    };
  };

  // Get element center
  const getElementCenter = (el) => ({
    x: el.x + el.width / 2,
    y: el.y + el.height / 2,
  });

  // Get connection points for an element (4 sides)
  const getConnectionPoints = (el) => {
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    return {
      top: { x: cx, y: el.y, side: 'top' },
      right: { x: el.x + el.width, y: cy, side: 'right' },
      bottom: { x: cx, y: el.y + el.height, side: 'bottom' },
      left: { x: el.x, y: cy, side: 'left' },
    };
  };

  // Find nearest connection point
  const findNearestConnectionPoint = (x, y, excludeElementId = null) => {
    let nearest = null;
    let minDist = 30; // Max distance to snap
    
    elements.forEach(el => {
      if (el.id === excludeElementId) return;
      if (el.type === ELEMENT_TYPES.CONNECTOR) return;
      
      const points = getConnectionPoints(el);
      Object.values(points).forEach(point => {
        const dist = Math.hypot(x - point.x, y - point.y);
        if (dist < minDist) {
          minDist = dist;
          nearest = { ...point, elementId: el.id };
        }
      });
    });
    
    return nearest;
  };

  // Find element at position
  const findElementAt = (x, y) => {
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      if (el.type === ELEMENT_TYPES.CONNECTOR) continue;
      if (x >= el.x && x <= el.x + el.width && y >= el.y && y <= el.y + el.height) {
        return el;
      }
    }
    return null;
  };

  // Get resize handle at position
  const getHandleAtPosition = (x, y, element) => {
    if (!element) return null;
    
    const handles = {
      nw: { x: element.x - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
      n: { x: element.x + element.width/2 - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
      ne: { x: element.x + element.width - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
      e: { x: element.x + element.width - HANDLE_SIZE/2, y: element.y + element.height/2 - HANDLE_SIZE/2 },
      se: { x: element.x + element.width - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
      s: { x: element.x + element.width/2 - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
      sw: { x: element.x - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
      w: { x: element.x - HANDLE_SIZE/2, y: element.y + element.height/2 - HANDLE_SIZE/2 },
    };
    
    for (const [handle, pos] of Object.entries(handles)) {
      if (x >= pos.x && x <= pos.x + HANDLE_SIZE && y >= pos.y && y <= pos.y + HANDLE_SIZE) {
        return handle;
      }
    }
    return null;
  };

  // Check if clicking on rotation handle
  const isOnRotationHandle = (x, y, element) => {
    if (!element) return false;
    const cx = element.x + element.width / 2;
    const ry = element.y - ROTATION_HANDLE_OFFSET;
    // Use larger detection radius for rotation handle (12px instead of 8px)
    return Math.hypot(x - cx, y - ry) < 12;
  };

  // Get cursor for handle
  const getCursorForHandle = (handle) => {
    const cursors = {
      nw: 'nwse-resize', ne: 'nesw-resize', se: 'nwse-resize', sw: 'nesw-resize',
      n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
    };
    return cursors[handle] || 'default';
  };

  // Draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Clear
    ctx.fillStyle = isDarkMode ? '#1a1a2e' : '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Grid
    if (showGrid) {
      ctx.strokeStyle = isDarkMode ? '#2a2a4e' : '#e5e5e5';
      ctx.lineWidth = 1;
      for (let x = 0; x <= width; x += gridSize * zoom) {
        ctx.beginPath();
        ctx.moveTo(x + (panX % (gridSize * zoom)), 0);
        ctx.lineTo(x + (panX % (gridSize * zoom)), height);
        ctx.stroke();
      }
      for (let y = 0; y <= height; y += gridSize * zoom) {
        ctx.beginPath();
        ctx.moveTo(0, y + (panY % (gridSize * zoom)));
        ctx.lineTo(width, y + (panY % (gridSize * zoom)));
        ctx.stroke();
      }
    }
    
    ctx.save();
    ctx.scale(zoom, zoom);
    ctx.translate(panX / zoom, panY / zoom);
    
    // Draw elements
    elements.forEach(element => {
      if (!element.visible) return;
      
      ctx.save();
      ctx.globalAlpha = element.opacity || 1;
      
      // Apply rotation
      if (element.rotation && element.type !== ELEMENT_TYPES.CONNECTOR) {
        const cx = element.x + element.width / 2;
        const cy = element.y + element.height / 2;
        ctx.translate(cx, cy);
        ctx.rotate((element.rotation * Math.PI) / 180);
        ctx.translate(-cx, -cy);
      }
      
      const strokeColor = element.strokeColor || '#1e1e1e';
      const fillColor = element.fillColor || 'transparent';
      const strokeWidth = element.strokeWidth || 2;
      
      switch (element.type) {
        case ELEMENT_TYPES.RECTANGLE:
          ctx.fillStyle = fillColor;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          if (fillColor !== 'transparent') ctx.fillRect(element.x, element.y, element.width, element.height);
          ctx.strokeRect(element.x, element.y, element.width, element.height);
          break;
          
        case ELEMENT_TYPES.DIAMOND:
          ctx.fillStyle = fillColor;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          const dcx = element.x + element.width / 2;
          const dcy = element.y + element.height / 2;
          ctx.beginPath();
          ctx.moveTo(dcx, element.y);
          ctx.lineTo(element.x + element.width, dcy);
          ctx.lineTo(dcx, element.y + element.height);
          ctx.lineTo(element.x, dcy);
          ctx.closePath();
          if (fillColor !== 'transparent') ctx.fill();
          ctx.stroke();
          break;
          
        case ELEMENT_TYPES.CIRCLE:
          ctx.fillStyle = fillColor;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          ctx.beginPath();
          ctx.ellipse(
            element.x + element.width / 2,
            element.y + element.height / 2,
            element.width / 2,
            element.height / 2,
            0, 0, Math.PI * 2
          );
          if (fillColor !== 'transparent') ctx.fill();
          ctx.stroke();
          break;
          
        case ELEMENT_TYPES.LINE:
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          ctx.beginPath();
          ctx.moveTo(element.x, element.y);
          ctx.lineTo(element.x + element.width, element.y + element.height);
          ctx.stroke();
          break;
          
        case ELEMENT_TYPES.ARROW:
          ctx.strokeStyle = strokeColor;
          ctx.fillStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          
          const ax1 = element.x;
          const ay1 = element.y;
          const ax2 = element.x + element.width;
          const ay2 = element.y + element.height;
          
          ctx.beginPath();
          ctx.moveTo(ax1, ay1);
          ctx.lineTo(ax2, ay2);
          ctx.stroke();
          
          // Arrowhead
          const angle = Math.atan2(ay2 - ay1, ax2 - ax1);
          const headLength = 12 + strokeWidth * 2;
          ctx.beginPath();
          ctx.moveTo(ax2, ay2);
          ctx.lineTo(ax2 - headLength * Math.cos(angle - Math.PI / 7), ay2 - headLength * Math.sin(angle - Math.PI / 7));
          ctx.lineTo(ax2 - headLength * Math.cos(angle + Math.PI / 7), ay2 - headLength * Math.sin(angle + Math.PI / 7));
          ctx.closePath();
          ctx.fill();
          break;
          
        case ELEMENT_TYPES.CONNECTOR:
          // Draw connector between elements
          ctx.strokeStyle = strokeColor;
          ctx.fillStyle = strokeColor;
          ctx.lineWidth = strokeWidth;
          
          let startX = element.startX || element.x;
          let startY = element.startY || element.y;
          let endX = element.endX || element.x + element.width;
          let endY = element.endY || element.y + element.height;
          
          // Update positions based on connected elements
          if (element.startElementId) {
            const startEl = elements.find(e => e.id === element.startElementId);
            if (startEl) {
              const points = getConnectionPoints(startEl);
              const point = points[element.startSide] || points.right;
              startX = point.x;
              startY = point.y;
            }
          }
          
          if (element.endElementId) {
            const endEl = elements.find(e => e.id === element.endElementId);
            if (endEl) {
              const points = getConnectionPoints(endEl);
              const point = points[element.endSide] || points.left;
              endX = point.x;
              endY = point.y;
            }
          }
          
          // Draw curved or straight connector
          ctx.beginPath();
          if (element.curved) {
            const midX = (startX + endX) / 2;
            const midY = (startY + endY) / 2;
            const ctrlX = midX;
            const ctrlY = startY;
            ctx.moveTo(startX, startY);
            ctx.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
          } else {
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
          }
          ctx.stroke();
          
          // Arrow at end
          const connAngle = Math.atan2(endY - startY, endX - startX);
          const connHeadLen = 10;
          ctx.beginPath();
          ctx.moveTo(endX, endY);
          ctx.lineTo(endX - connHeadLen * Math.cos(connAngle - Math.PI / 6), endY - connHeadLen * Math.sin(connAngle - Math.PI / 6));
          ctx.lineTo(endX - connHeadLen * Math.cos(connAngle + Math.PI / 6), endY - connHeadLen * Math.sin(connAngle + Math.PI / 6));
          ctx.closePath();
          ctx.fill();
          break;
          
        case ELEMENT_TYPES.TEXT:
          ctx.fillStyle = strokeColor;
          ctx.font = `${element.fontSize || 16}px ${element.fontFamily || 'Inter, sans-serif'}`;
          ctx.fillText(element.text || 'Texte', element.x, element.y + (element.fontSize || 16));
          break;
          
        case ELEMENT_TYPES.ICON:
          if (element.iconUrl) {
            const cachedImg = loadedImages[element.iconUrl];
            if (cachedImg && cachedImg.complete && cachedImg.naturalHeight !== 0) {
              ctx.drawImage(cachedImg, element.x, element.y, element.width, element.height);
            } else if (!loadedImages[element.iconUrl]) {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => setLoadedImages(prev => ({ ...prev, [element.iconUrl]: img }));
              img.src = element.iconUrl;
              setLoadedImages(prev => ({ ...prev, [element.iconUrl]: img }));
            }
          }
          // Draw label
          if (element.label) {
            ctx.fillStyle = strokeColor;
            ctx.font = '12px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(element.label, element.x + element.width / 2, element.y + element.height + 16);
            ctx.textAlign = 'left';
          }
          break;
          
        case ELEMENT_TYPES.IMAGE:
          if (element.imageUrl) {
            const cachedImg = loadedImages[element.imageUrl];
            if (cachedImg && cachedImg.complete && cachedImg.naturalHeight !== 0) {
              ctx.drawImage(cachedImg, element.x, element.y, element.width, element.height);
            } else if (!loadedImages[element.imageUrl]) {
              const img = new Image();
              img.onload = () => setLoadedImages(prev => ({ ...prev, [element.imageUrl]: img }));
              img.src = element.imageUrl;
              setLoadedImages(prev => ({ ...prev, [element.imageUrl]: img }));
            }
          }
          break;
          
        case ELEMENT_TYPES.FREEHAND:
          if (element.points && element.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(element.points[0].x, element.points[0].y);
            for (let i = 1; i < element.points.length; i++) {
              ctx.lineTo(element.points[i].x, element.points[i].y);
            }
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = strokeWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.stroke();
          }
          break;
      }
      
      ctx.restore();
      
      // Selection box and handles
      if (selectedIds.includes(element.id) && element.type !== ELEMENT_TYPES.CONNECTOR) {
        ctx.save();
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 5]);
        ctx.strokeRect(element.x - 4, element.y - 4, element.width + 8, element.height + 8);
        ctx.setLineDash([]);
        
        // Resize handles
        const handlePositions = [
          { x: element.x - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
          { x: element.x + element.width/2 - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
          { x: element.x + element.width - HANDLE_SIZE/2, y: element.y - HANDLE_SIZE/2 },
          { x: element.x + element.width - HANDLE_SIZE/2, y: element.y + element.height/2 - HANDLE_SIZE/2 },
          { x: element.x + element.width - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
          { x: element.x + element.width/2 - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
          { x: element.x - HANDLE_SIZE/2, y: element.y + element.height - HANDLE_SIZE/2 },
          { x: element.x - HANDLE_SIZE/2, y: element.y + element.height/2 - HANDLE_SIZE/2 },
        ];
        
        handlePositions.forEach(pos => {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(pos.x, pos.y, HANDLE_SIZE, HANDLE_SIZE);
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 1;
          ctx.strokeRect(pos.x, pos.y, HANDLE_SIZE, HANDLE_SIZE);
        });
        
        // Rotation handle
        const rcx = element.x + element.width / 2;
        const rcy = element.y - ROTATION_HANDLE_OFFSET;
        ctx.beginPath();
        ctx.moveTo(rcx, element.y - 4);
        ctx.lineTo(rcx, rcy + 5);
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 1;
        ctx.stroke();
        
        ctx.beginPath();
        ctx.arc(rcx, rcy, 5, 0, Math.PI * 2);
        ctx.fillStyle = '#3b82f6';
        ctx.fill();
        
        ctx.restore();
      }
    });
    
    // Draw connection points on hovered element
    if (hoveredElement && activeTool === TOOL_TYPES.CONNECTOR) {
      const points = getConnectionPoints(hoveredElement);
      Object.values(points).forEach(point => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, CONNECTION_POINT_SIZE, 0, Math.PI * 2);
        ctx.fillStyle = '#22c55e';
        ctx.fill();
        ctx.strokeStyle = '#166534';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
    }
    
    // Draw current connector being created
    if (isConnecting && connectionStart) {
      ctx.beginPath();
      ctx.moveTo(connectionStart.x, connectionStart.y);
      ctx.lineTo(connectionEnd?.x || connectionStart.x, connectionEnd?.y || connectionStart.y);
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    
    // Current shape being drawn
    if (currentShape) {
      ctx.save();
      ctx.strokeStyle = activeStyles.strokeColor || '#1e1e1e';
      ctx.lineWidth = activeStyles.strokeWidth || 2;
      ctx.setLineDash([5, 5]);
      
      switch (currentShape.type) {
        case ELEMENT_TYPES.RECTANGLE:
          ctx.strokeRect(currentShape.x, currentShape.y, currentShape.width, currentShape.height);
          break;
        case ELEMENT_TYPES.DIAMOND:
          const cdx = currentShape.x + currentShape.width / 2;
          const cdy = currentShape.y + currentShape.height / 2;
          ctx.beginPath();
          ctx.moveTo(cdx, currentShape.y);
          ctx.lineTo(currentShape.x + currentShape.width, cdy);
          ctx.lineTo(cdx, currentShape.y + currentShape.height);
          ctx.lineTo(currentShape.x, cdy);
          ctx.closePath();
          ctx.stroke();
          break;
        case ELEMENT_TYPES.CIRCLE:
          ctx.beginPath();
          ctx.ellipse(
            currentShape.x + currentShape.width / 2,
            currentShape.y + currentShape.height / 2,
            Math.abs(currentShape.width) / 2,
            Math.abs(currentShape.height) / 2,
            0, 0, Math.PI * 2
          );
          ctx.stroke();
          break;
        case ELEMENT_TYPES.LINE:
        case ELEMENT_TYPES.ARROW:
          ctx.beginPath();
          ctx.moveTo(currentShape.x, currentShape.y);
          ctx.lineTo(currentShape.x + currentShape.width, currentShape.y + currentShape.height);
          ctx.stroke();
          break;
      }
      ctx.setLineDash([]);
      ctx.restore();
    }
    
    // Freehand drawing
    if (activeTool === TOOL_TYPES.FREEHAND && freehandPoints.length > 1) {
      ctx.beginPath();
      ctx.moveTo(freehandPoints[0].x, freehandPoints[0].y);
      for (let i = 1; i < freehandPoints.length; i++) {
        ctx.lineTo(freehandPoints[i].x, freehandPoints[i].y);
      }
      ctx.strokeStyle = activeStyles.strokeColor || '#1e1e1e';
      ctx.lineWidth = activeStyles.strokeWidth || 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
    
    // Collaborator cursors
    Object.values(collaborators).forEach(collab => {
      if (!collab.cursor) return;
      ctx.save();
      ctx.translate(collab.cursor.x, collab.cursor.y);
      ctx.fillStyle = collab.color || '#3b82f6';
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, 16);
      ctx.lineTo(4, 14);
      ctx.lineTo(9, 22);
      ctx.lineTo(12, 20);
      ctx.lineTo(7, 12);
      ctx.lineTo(12, 12);
      ctx.closePath();
      ctx.fill();
      ctx.font = '12px Inter, sans-serif';
      const textWidth = ctx.measureText(collab.name || 'User').width;
      ctx.fillRect(14, 10, textWidth + 8, 18);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(collab.name || 'User', 18, 23);
      ctx.restore();
    });
    
    ctx.restore();
  }, [elements, selectedIds, currentShape, zoom, panX, panY, showGrid, gridSize, isDarkMode, activeStyles, collaborators, freehandPoints, activeTool, loadedImages, hoveredElement, isConnecting, connectionStart, connectionEnd]);

  // Resize canvas
  useEffect(() => {
    const handleResize = () => {
      if (!canvasRef.current || !containerRef.current) return;
      canvasRef.current.width = containerRef.current.clientWidth;
      canvasRef.current.height = containerRef.current.clientHeight;
      draw();
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [draw]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if editing text
      if (editingText) return;
      
      // Ctrl+A or Cmd+A to select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        selectAll();
      }
      
      // Delete or Backspace to delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        deleteElements(selectedIds);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectAll, deleteElements, selectedIds, editingText]);

  // Mouse handlers
  const handleMouseDown = (e) => {
    const pos = getMousePos(e);
    setStartPoint(pos);
    
    // Connector tool
    if (activeTool === TOOL_TYPES.CONNECTOR) {
      const connectionPoint = findNearestConnectionPoint(pos.x, pos.y);
      if (connectionPoint) {
        setIsConnecting(true);
        setConnectionStart(connectionPoint);
        setConnectionEnd(pos);
      }
      return;
    }
    
    // Check rotation handle
    if (selectedElements.length === 1) {
      if (isOnRotationHandle(pos.x, pos.y, selectedElements[0])) {
        setIsRotating(true);
        return;
      }
      
      const handle = getHandleAtPosition(pos.x, pos.y, selectedElements[0]);
      if (handle) {
        setIsResizing(true);
        setResizeHandle(handle);
        return;
      }
    }
    
    // Select tool
    if (activeTool === TOOL_TYPES.SELECT) {
      const element = findElementAt(pos.x, pos.y);
      if (element) {
        if (!selectedIds.includes(element.id)) {
          if (e.shiftKey) {
            setSelectedIds([...selectedIds, element.id]);
          } else {
            setSelectedIds([element.id]);
          }
        }
        setIsDragging(true);
      } else {
        clearSelection();
      }
      return;
    }
    
    // Drawing tools
    if ([TOOL_TYPES.RECTANGLE, TOOL_TYPES.CIRCLE, TOOL_TYPES.LINE, TOOL_TYPES.ARROW, TOOL_TYPES.DIAMOND].includes(activeTool)) {
      setIsDrawing(true);
      let type;
      switch (activeTool) {
        case TOOL_TYPES.RECTANGLE: type = ELEMENT_TYPES.RECTANGLE; break;
        case TOOL_TYPES.CIRCLE: type = ELEMENT_TYPES.CIRCLE; break;
        case TOOL_TYPES.LINE: type = ELEMENT_TYPES.LINE; break;
        case TOOL_TYPES.ARROW: type = ELEMENT_TYPES.ARROW; break;
        case TOOL_TYPES.DIAMOND: type = ELEMENT_TYPES.DIAMOND; break;
        default: type = ELEMENT_TYPES.RECTANGLE;
      }
      setCurrentShape({ type, x: pos.x, y: pos.y, width: 0, height: 0 });
    }
    
    // Text tool - create inline input
    if (activeTool === TOOL_TYPES.TEXT) {
      // Don't create new text input if already editing
      if (editingText) return;
      
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();
      setEditingText({
        elementId: null,
        type: 'new_text',
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        canvasX: pos.x,
        canvasY: pos.y,
        value: '',
      });
      return;
    }
    
    // Freehand
    if (activeTool === TOOL_TYPES.FREEHAND) {
      setIsDrawing(true);
      setFreehandPoints([{ x: pos.x, y: pos.y }]);
    }
    
    // Eraser
    if (activeTool === TOOL_TYPES.ERASER) {
      const element = findElementAt(pos.x, pos.y);
      if (element) deleteElements([element.id]);
    }
  };

  const handleMouseMove = (e) => {
    const pos = getMousePos(e);
    
    // Update cursor
    if (canvasRef.current) {
      let cursor = 'crosshair';
      if (activeTool === TOOL_TYPES.SELECT) {
        cursor = 'default';
        if (selectedElements.length === 1) {
          if (isOnRotationHandle(pos.x, pos.y, selectedElements[0])) {
            cursor = 'grab';
          } else {
            const handle = getHandleAtPosition(pos.x, pos.y, selectedElements[0]);
            if (handle) cursor = getCursorForHandle(handle);
            else if (findElementAt(pos.x, pos.y)) cursor = 'move';
          }
        } else if (findElementAt(pos.x, pos.y)) {
          cursor = 'move';
        }
      } else if (activeTool === TOOL_TYPES.CONNECTOR) {
        cursor = 'crosshair';
        setHoveredElement(findElementAt(pos.x, pos.y));
      }
      canvasRef.current.style.cursor = cursor;
    }
    
    if (onCursorMove) onCursorMove(pos);
    
    // Connector drawing
    if (isConnecting) {
      const nearPoint = findNearestConnectionPoint(pos.x, pos.y, connectionStart?.elementId);
      setConnectionEnd(nearPoint || pos);
      return;
    }
    
    // Rotation
    if (isRotating && selectedElements.length === 1 && startPoint) {
      const el = selectedElements[0];
      const center = getElementCenter(el);
      const angle = Math.atan2(pos.y - center.y, pos.x - center.x) * (180 / Math.PI) + 90;
      updateElement(el.id, { rotation: angle });
      return;
    }
    
    // Resizing
    if (isResizing && selectedElements.length === 1 && startPoint) {
      const el = selectedElements[0];
      const dx = pos.x - startPoint.x;
      const dy = pos.y - startPoint.y;
      
      let newX = el.x, newY = el.y, newW = el.width, newH = el.height;
      
      switch (resizeHandle) {
        case 'nw': newX = el.x + dx; newY = el.y + dy; newW = el.width - dx; newH = el.height - dy; break;
        case 'n': newY = el.y + dy; newH = el.height - dy; break;
        case 'ne': newY = el.y + dy; newW = el.width + dx; newH = el.height - dy; break;
        case 'e': newW = el.width + dx; break;
        case 'se': newW = el.width + dx; newH = el.height + dy; break;
        case 's': newH = el.height + dy; break;
        case 'sw': newX = el.x + dx; newW = el.width - dx; newH = el.height + dy; break;
        case 'w': newX = el.x + dx; newW = el.width - dx; break;
      }
      
      if (newW < 10) { newW = 10; if (['nw', 'w', 'sw'].includes(resizeHandle)) newX = el.x + el.width - 10; }
      if (newH < 10) { newH = 10; if (['nw', 'n', 'ne'].includes(resizeHandle)) newY = el.y + el.height - 10; }
      
      updateElement(el.id, { x: newX, y: newY, width: newW, height: newH });
      setStartPoint(pos);
      return;
    }
    
    // Dragging
    if (isDragging && selectedIds.length > 0 && startPoint) {
      const dx = pos.x - startPoint.x;
      const dy = pos.y - startPoint.y;
      selectedElements.forEach(el => {
        updateElement(el.id, { x: el.x + dx, y: el.y + dy });
      });
      setStartPoint(pos);
      return;
    }
    
    // Freehand
    if (activeTool === TOOL_TYPES.FREEHAND && isDrawing) {
      setFreehandPoints(prev => [...prev, { x: pos.x, y: pos.y }]);
      return;
    }
    
    // Eraser
    if (activeTool === TOOL_TYPES.ERASER && e.buttons === 1) {
      const element = findElementAt(pos.x, pos.y);
      if (element) deleteElements([element.id]);
      return;
    }
    
    // Drawing shapes
    if (!isDrawing || !startPoint) return;
    
    const width = pos.x - startPoint.x;
    const height = pos.y - startPoint.y;
    
    setCurrentShape(prev => prev ? {
      ...prev,
      x: width < 0 ? pos.x : startPoint.x,
      y: height < 0 ? pos.y : startPoint.y,
      width: Math.abs(width),
      height: Math.abs(height),
    } : null);
  };

  const handleMouseUp = (e) => {
    const pos = getMousePos(e);
    
    // Finish connector
    if (isConnecting && connectionStart) {
      const endPoint = findNearestConnectionPoint(pos.x, pos.y, connectionStart.elementId);
      if (endPoint) {
        addElement(ELEMENT_TYPES.CONNECTOR, {
          x: connectionStart.x,
          y: connectionStart.y,
          width: endPoint.x - connectionStart.x,
          height: endPoint.y - connectionStart.y,
          startElementId: connectionStart.elementId,
          startSide: connectionStart.side,
          endElementId: endPoint.elementId,
          endSide: endPoint.side,
          startX: connectionStart.x,
          startY: connectionStart.y,
          endX: endPoint.x,
          endY: endPoint.y,
        });
      }
      setIsConnecting(false);
      setConnectionStart(null);
      setConnectionEnd(null);
      return;
    }
    
    if (isRotating) { setIsRotating(false); return; }
    if (isResizing) { setIsResizing(false); setResizeHandle(null); setStartPoint(null); return; }
    if (isDragging) { setIsDragging(false); setStartPoint(null); return; }
    
    // Freehand
    if (activeTool === TOOL_TYPES.FREEHAND && isDrawing && freehandPoints.length > 1) {
      const minX = Math.min(...freehandPoints.map(p => p.x));
      const maxX = Math.max(...freehandPoints.map(p => p.x));
      const minY = Math.min(...freehandPoints.map(p => p.y));
      const maxY = Math.max(...freehandPoints.map(p => p.y));
      addElement(ELEMENT_TYPES.FREEHAND, {
        x: minX, y: minY, width: maxX - minX || 10, height: maxY - minY || 10, points: freehandPoints,
      });
      setFreehandPoints([]);
      setIsDrawing(false);
      return;
    }
    
    // Shape completion
    if (!isDrawing || !currentShape) { setIsDrawing(false); return; }
    
    if (currentShape.width > 5 || currentShape.height > 5) {
      let { x, y, width, height } = currentShape;
      if (snapToGrid) {
        x = Math.round(x / gridSize) * gridSize;
        y = Math.round(y / gridSize) * gridSize;
        width = Math.round(width / gridSize) * gridSize || gridSize;
        height = Math.round(height / gridSize) * gridSize || gridSize;
      }
      addElement(currentShape.type, { x, y, width, height });
    }
    
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentShape(null);
  };

  // Double-click for text editing on icons
  const handleDoubleClick = (e) => {
    const pos = getMousePos(e);
    const element = findElementAt(pos.x, pos.y);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (element && element.type === ELEMENT_TYPES.ICON) {
      setEditingText({
        elementId: element.id,
        type: 'icon',
        x: (element.x + element.width / 2) * zoom + panX + rect.left - 75,
        y: (element.y + element.height + 20) * zoom + panY + rect.top,
        value: element.label || element.iconName || '',
      });
    } else if (element && element.type === ELEMENT_TYPES.TEXT) {
      setEditingText({
        elementId: element.id,
        type: 'text',
        x: element.x * zoom + panX + rect.left,
        y: element.y * zoom + panY + rect.top,
        value: element.text || '',
      });
    }
  };

  // Handle text input submission
  const handleTextSubmit = () => {
    if (!editingText) return;
    
    // Get value directly from the input element
    const inputValue = textInputRef.current?.value || editingText.value || '';
    const { elementId, type, canvasX, canvasY } = editingText;
    
    if (type === 'new_text' && inputValue.trim()) {
      addElement(ELEMENT_TYPES.TEXT, {
        x: canvasX,
        y: canvasY,
        width: Math.max(inputValue.length * 10, 50),
        height: 24,
        text: inputValue,
      });
    } else if (type === 'icon' && elementId) {
      updateElement(elementId, { label: inputValue });
    } else if (type === 'text' && elementId) {
      updateElement(elementId, { text: inputValue, width: Math.max(inputValue.length * 10, 50) });
    }
    
    setEditingText(null);
  };

  // Handle text input cancel
  const handleTextCancel = () => {
    setEditingText(null);
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingText && textInputRef.current) {
      textInputRef.current.focus();
      textInputRef.current.select();
    }
  }, [editingText]);

  // Drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    const pos = getMousePos(e);
    
    if (e.dataTransfer.files?.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = new Image();
          img.onload = () => {
            const maxSize = 300;
            let w = img.width, h = img.height;
            if (w > maxSize || h > maxSize) {
              if (w > h) { h = (h / w) * maxSize; w = maxSize; }
              else { w = (w / h) * maxSize; h = maxSize; }
            }
            addElement(ELEMENT_TYPES.IMAGE, { x: pos.x - w/2, y: pos.y - h/2, width: w, height: h, imageUrl: event.target.result });
          };
          img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        return;
      }
    }
    
    try {
      const iconData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (iconData) {
        addElement(ELEMENT_TYPES.ICON, {
          x: pos.x - 32, y: pos.y - 32, width: 64, height: 64,
          iconUrl: iconData.iconUrl, iconId: iconData.id, iconName: iconData.name,
        });
      }
    } catch (err) {}
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative w-full h-full overflow-hidden", isDarkMode ? "bg-[#1a1a2e]" : "bg-white", className)}
    >
      <canvas
        ref={canvasRef}
        data-testid="draw-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      />
      
      {/* Inline text editing overlay */}
      {editingText && (
        <div
          className="absolute flex items-center gap-1 z-50"
          style={{
            left: editingText.x,
            top: editingText.y,
          }}
          data-testid="text-editor-overlay"
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <input
            ref={textInputRef}
            type="text"
            defaultValue={editingText.value}
            onBlur={(e) => {
              // Update state on blur
              setEditingText(prev => prev ? { ...prev, value: e.target.value } : null);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                // Get value from input directly
                const value = e.currentTarget.value;
                setEditingText(prev => prev ? { ...prev, value } : null);
                setTimeout(() => handleTextSubmit(), 0);
              }
              if (e.key === 'Escape') handleTextCancel();
            }}
            className="w-[200px] h-8 px-3 text-sm bg-white border-2 border-primary rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={editingText.type === 'icon' ? 'Label...' : 'Texte...'}
            data-testid="text-input"
            autoFocus
          />
          <Button
            size="icon"
            variant="default"
            className="h-8 w-8"
            onClick={handleTextSubmit}
            data-testid="text-submit-btn"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            className="h-8 w-8"
            onClick={handleTextCancel}
            data-testid="text-cancel-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
